import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import {
  DocumentTextIcon,
  CheckCircleIcon,
  XCircleIcon,
  MagnifyingGlassIcon,
  ClockIcon,
  BuildingOfficeIcon,
  UserIcon,
  ShieldCheckIcon,
  ExclamationTriangleIcon,
  FunnelIcon,
  DocumentArrowDownIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline'
import LoadingSpinner from '../../../components/LoadingSpinner'
import ProviderDocumentReviewPanel from '../../../components/admin/ProviderDocumentReviewPanel'
import UserDocumentsSection from '../../../components/admin/UserDocumentsSection'
import { DocumentStatus, ServiceType, ServiceProviderDocument } from '../../../types'
import { useAdminAllDocuments, useVerifyProviderDocument, useRejectProviderDocument } from '../../../hooks/useDocuments'

const serviceTypeColors: Record<ServiceType, string> = {
  AMBULANCE_TRANSPORT: 'bg-blue-100 text-blue-800',
  GHUSL_SERVICE: 'bg-purple-100 text-purple-800',
  KAFAN_SERVICE: 'bg-indigo-100 text-indigo-800',
  JANAZAH_LEAD: 'bg-pink-100 text-pink-800',
  BURIAL_PLOT: 'bg-green-100 text-green-800',
  CATERING: 'bg-orange-100 text-orange-800',
  FLOWERS: 'bg-yellow-100 text-yellow-800',
  TENT_RENTAL: 'bg-teal-100 text-teal-800',
  PHOTOGRAPHY: 'bg-cyan-100 text-cyan-800',
  YASIN_BOOKS: 'bg-lime-100 text-lime-800',
  ISLAMIC_SOUVENIRS: 'bg-amber-100 text-amber-800',
  // Provider types (legacy support)
  AMBULANCE_PROVIDER: 'bg-blue-100 text-blue-800',
  USTADZ: 'bg-purple-100 text-purple-800',
  CEMETERY_PROVIDER: 'bg-green-100 text-green-800',
  FUNERAL_HOME: 'bg-orange-100 text-orange-800',
  VENDOR: 'bg-gray-100 text-gray-800',
  PROVIDER: 'bg-gray-100 text-gray-800'
}

const serviceTypeLabels: Record<ServiceType, string> = {
  AMBULANCE_TRANSPORT: 'Ambulance Transport',
  GHUSL_SERVICE: 'Ghusl Service',
  KAFAN_SERVICE: 'Kafan Service',
  JANAZAH_LEAD: 'Janazah Lead',
  BURIAL_PLOT: 'Burial Plot',
  CATERING: 'Catering',
  FLOWERS: 'Flowers',
  TENT_RENTAL: 'Tent Rental',
  PHOTOGRAPHY: 'Photography',
  YASIN_BOOKS: 'Yasin Books',
  ISLAMIC_SOUVENIRS: 'Islamic Souvenirs',
  // Provider types (legacy support)
  AMBULANCE_PROVIDER: 'Ambulance',
  USTADZ: 'Ustadz',
  CEMETERY_PROVIDER: 'Cemetery',
  FUNERAL_HOME: 'Funeral Home',
  VENDOR: 'Vendor',
  PROVIDER: 'General'
}

const statusColors: Record<DocumentStatus, string> = {
  PENDING: 'bg-yellow-100 text-yellow-800',
  UPLOADED: 'bg-blue-100 text-blue-800',
  VERIFIED: 'bg-green-100 text-green-800',
  REJECTED: 'bg-red-100 text-red-800'
}

export default function ProviderDocumentVerificationPage() {
  const { data: documents = [], isLoading, error, refetch } = useAdminAllDocuments()
  const [selectedDocument, setSelectedDocument] = useState<ServiceProviderDocument | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<DocumentStatus | 'all'>('all')
  const [serviceTypeFilter, setServiceTypeFilter] = useState<ServiceType | 'all'>('all')
  const [expandedProviders, setExpandedProviders] = useState<Set<string>>(new Set())
  const [docTab, setDocTab] = useState<'provider' | 'user'>('provider')

  const verifyDocumentMutation = useVerifyProviderDocument()
  const rejectDocumentMutation = useRejectProviderDocument()

  // Function to group documents by provider
  const groupDocumentsByProvider = (docs: ServiceProviderDocument[]) => {
    const groups = new Map<string, {
      provider: ServiceProviderDocument['serviceProvider']
      documents: ServiceProviderDocument[]
      stats: {
        total: number
        pending: number
        uploaded: number
        verified: number
        rejected: number
      }
    }>()

    docs.forEach(doc => {
      const providerId = doc.serviceProvider.id
      if (!groups.has(providerId)) {
        groups.set(providerId, {
          provider: doc.serviceProvider,
          documents: [],
          stats: { total: 0, pending: 0, uploaded: 0, verified: 0, rejected: 0 }
        })
      }

      const group = groups.get(providerId)!
      group.documents.push(doc)
      group.stats.total++
      group.stats[doc.status.toLowerCase() as keyof typeof group.stats]++
    })

    return Array.from(groups.values()).sort((a, b) =>
      a.provider.companyName.localeCompare(b.provider.companyName)
    )
  }

  // Filter documents first, then group by provider
  const filteredDocuments = documents.filter(doc => {
    const matchesSearch =
      doc.originalName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      doc.serviceProvider.companyName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      `${doc.serviceProvider.user.firstName} ${doc.serviceProvider.user.lastName}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
      doc.serviceProvider.user.email.toLowerCase().includes(searchTerm.toLowerCase())

    const matchesStatus = statusFilter === 'all' || doc.status === statusFilter
    const matchesServiceType = serviceTypeFilter === 'all' || doc.serviceProvider.serviceType === serviceTypeFilter

    return matchesSearch && matchesStatus && matchesServiceType
  })

  const groupedProviders = groupDocumentsByProvider(filteredDocuments)

  // Providers are collapsed by default - no auto-expansion
  // Users can manually expand providers they want to review

  const toggleProviderExpansion = (providerId: string) => {
    const newExpanded = new Set(expandedProviders)
    if (newExpanded.has(providerId)) {
      newExpanded.delete(providerId)
    } else {
      newExpanded.add(providerId)
    }
    setExpandedProviders(newExpanded)
  }

  const expandAllProviders = () => {
    const allProviderIds = new Set(groupedProviders.map(g => g.provider.id))
    setExpandedProviders(allProviderIds)
  }

  const collapseAllProviders = () => {
    setExpandedProviders(new Set())
  }

  const handleDocumentReview = () => {
    setSelectedDocument(null)
    // Refetch documents to get updated data
    refetch()
  }

  const getStats = () => {
    return {
      total: documents.length,
      pending: documents.filter(d => d.status === 'PENDING').length,
      uploaded: documents.filter(d => d.status === 'UPLOADED').length,
      verified: documents.filter(d => d.status === 'VERIFIED').length,
      rejected: documents.filter(d => d.status === 'REJECTED').length
    }
  }

  const stats = getStats()

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-red-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Error loading documents</h3>
          <p className="mt-1 text-sm text-gray-500">
            Failed to load provider documents. Please try again later.
          </p>
          <button
            onClick={() => refetch()}
            className="mt-2 inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            Retry
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Document Verification</h1>
          <p className="text-gray-600 mt-1">Review and verify document submissions</p>
        </div>
        <Link
          to="/admin/providers"
          className="text-primary-600 hover:text-primary-700 font-medium"
        >
          Back to Providers
        </Link>
      </div>

      {/* Tab toggle */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8" aria-label="Tabs">
          <button
            type="button"
            onClick={() => setDocTab('provider')}
            className={`${
              docTab === 'provider'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
            } whitespace-nowrap border-b-2 py-3 px-1 text-sm font-medium`}
          >
            Provider Documents
          </button>
          <button
            type="button"
            onClick={() => setDocTab('user')}
            className={`${
              docTab === 'user'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
            } whitespace-nowrap border-b-2 py-3 px-1 text-sm font-medium`}
          >
            User Documents
          </button>
        </nav>
      </div>

      {/* User Documents Tab */}
      {docTab === 'user' && <UserDocumentsSection />}

      {/* Provider Documents Tab */}
      {docTab === 'provider' && (
      <>
      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center">
            <DocumentTextIcon className="h-8 w-8 text-gray-600" />
            <div className="ml-4">
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
              <p className="text-gray-600">Total Documents</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center">
            <ClockIcon className="h-8 w-8 text-yellow-600" />
            <div className="ml-4">
              <p className="text-2xl font-bold text-yellow-600">{stats.pending}</p>
              <p className="text-gray-600">Pending Review</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center">
            <ShieldCheckIcon className="h-8 w-8 text-blue-600" />
            <div className="ml-4">
              <p className="text-2xl font-bold text-blue-600">{stats.uploaded}</p>
              <p className="text-gray-600">Uploaded</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center">
            <CheckCircleIcon className="h-8 w-8 text-green-600" />
            <div className="ml-4">
              <p className="text-2xl font-bold text-green-600">{stats.verified}</p>
              <p className="text-gray-600">Verified</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center">
            <XCircleIcon className="h-8 w-8 text-red-600" />
            <div className="ml-4">
              <p className="text-2xl font-bold text-red-600">{stats.rejected}</p>
              <p className="text-gray-600">Rejected</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                placeholder="Search by document, provider, or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as DocumentStatus | 'all')}
              className="block w-full pl-3 pr-10 py-2 border border-gray-300 rounded-md leading-5 bg-white focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="all">All Status</option>
              <option value="PENDING">Pending</option>
              <option value="UPLOADED">Uploaded</option>
              <option value="VERIFIED">Verified</option>
              <option value="REJECTED">Rejected</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Service Type</label>
            <select
              value={serviceTypeFilter}
              onChange={(e) => setServiceTypeFilter(e.target.value as ServiceType | 'all')}
              className="block w-full pl-3 pr-10 py-2 border border-gray-300 rounded-md leading-5 bg-white focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="all">All Services</option>
              <option value="AMBULANCE_PROVIDER">Ambulance</option>
              <option value="USTADZ">Ustadz</option>
              <option value="CEMETERY_PROVIDER">Cemetery</option>
              <option value="FUNERAL_HOME">Funeral Home</option>
              <option value="VENDOR">Vendor</option>
            </select>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Documents List */}
        <div>
          {/* Header */}
          <div className="mb-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-medium text-gray-900">
                  Providers ({groupedProviders.length})
                </h2>
                <p className="text-sm text-gray-500">
                  {filteredDocuments.length} total documents
                </p>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={expandAllProviders}
                  className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                >
                  Expand All
                </button>
                <span className="text-gray-300">|</span>
                <button
                  onClick={collapseAllProviders}
                  className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                >
                  Collapse All
                </button>
              </div>
            </div>
          </div>

          {/* Provider Cards */}
          <div className="space-y-4 max-h-[800px] overflow-y-auto pr-2">
            {groupedProviders.length === 0 ? (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
                <DocumentArrowDownIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No documents found</h3>
                <p className="mt-1 text-sm text-gray-500">
                  {searchTerm || statusFilter !== 'all' || serviceTypeFilter !== 'all'
                    ? 'No documents match your search criteria'
                    : 'No documents have been uploaded yet'}
                </p>
              </div>
            ) : (
              groupedProviders.map((providerGroup) => (
                <div key={providerGroup.provider.id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                  {/* Provider Card Header */}
                  <div
                    className="p-4 hover:bg-gray-50 transition-colors cursor-pointer"
                    onClick={() => toggleProviderExpansion(providerGroup.provider.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3 flex-1 min-w-0">
                        <div className="flex-shrink-0">
                          {expandedProviders.has(providerGroup.provider.id) ? (
                            <ChevronDownIcon className="h-5 w-5 text-gray-600" />
                          ) : (
                            <ChevronRightIcon className="h-5 w-5 text-gray-600" />
                          )}
                        </div>
                        <div className="flex-shrink-0">
                          <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                            <UserGroupIcon className="h-5 w-5 text-primary-600" />
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-3">
                            <h3 className="text-sm font-medium text-gray-900 truncate">
                              {providerGroup.provider.companyName}
                            </h3>
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${serviceTypeColors[providerGroup.provider.serviceType]}`}>
                              {serviceTypeLabels[providerGroup.provider.serviceType]}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Provider Stats */}
                      <div className="flex items-center space-x-4 text-xs ml-4">
                        <div className="text-center">
                          <div className="font-medium text-gray-900">{providerGroup.stats.total}</div>
                          <div className="text-gray-500">Total</div>
                        </div>
                        <div className="text-center">
                          <div className="font-medium text-yellow-600">{providerGroup.stats.pending + providerGroup.stats.uploaded}</div>
                          <div className="text-gray-500">Pending</div>
                        </div>
                        <div className="text-center">
                          <div className="font-medium text-green-600">{providerGroup.stats.verified}</div>
                          <div className="text-gray-500">Verified</div>
                        </div>
                        <div className="text-center">
                          <div className="font-medium text-red-600">{providerGroup.stats.rejected}</div>
                          <div className="text-gray-500">Rejected</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Documents for this Provider */}
                  {expandedProviders.has(providerGroup.provider.id) && (
                    <div className="border-t border-gray-200 bg-gray-50">
                      <div className="px-4 py-3 bg-gray-100 border-b border-gray-200">
                        <p className="text-xs font-medium text-gray-700">
                          Documents ({providerGroup.documents.length})
                        </p>
                      </div>
                      {providerGroup.documents.length === 0 ? (
                        <div className="px-6 py-8 text-center">
                          <DocumentTextIcon className="mx-auto h-8 w-8 text-gray-400" />
                          <p className="mt-2 text-sm text-gray-500">No documents for this provider</p>
                        </div>
                      ) : (
                        <div className="divide-y divide-gray-200">
                          {providerGroup.documents.map((document) => (
                            <div
                              key={document.id}
                              className={`p-4 hover:bg-white transition-all duration-200 border-l-4 cursor-pointer ${
                                selectedDocument?.id === document.id
                                  ? 'bg-white shadow-sm'
                                  : 'border-transparent hover:bg-white'
                              }`}
                              style={{
                                borderLeftColor: selectedDocument?.id === document.id ? 'rgb(34 197 94)' : 'transparent',
                                borderLeftWidth: '4px'
                              }}
                              onClick={(e) => {
                                e.stopPropagation()
                                setSelectedDocument(document)
                              }}
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center space-x-3">
                                    <DocumentTextIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                                    <div className="flex-1 min-w-0">
                                      <h4 className="text-sm font-medium text-gray-900 truncate">
                                        {document.originalName}
                                      </h4>
                                      <div className="flex items-center space-x-2 mt-1">
                                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[document.status]}`}>
                                          {document.status.replace(/_/g, ' ')}
                                        </span>
                                        <span className="text-xs text-gray-500">
                                          {(document.fileSize / 1024).toFixed(1)} KB
                                        </span>
                                        <span className="text-xs text-gray-500">
                                          {new Date(document.uploadedAt).toLocaleDateString()}
                                        </span>
                                        {document.verifiedAt && (
                                          <span className="text-xs text-green-600">
                                            ✓ Verified {new Date(document.verifiedAt).toLocaleDateString()}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>

                              {document.verificationNotes && (
                                <div className="mt-3 text-xs text-gray-600 bg-white p-3 rounded border">
                                  <strong>Notes:</strong> {document.verificationNotes}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>

        {/* Document Review Panel */}
        <div className="lg:sticky lg:top-6">
          {selectedDocument ? (
            <ProviderDocumentReviewPanel
              document={selectedDocument}
              onVerify={(verificationNotes) => {
                verifyDocumentMutation.mutate(
                  { id: selectedDocument.id, verificationNotes },
                  {
                    onSuccess: () => {
                      handleDocumentReview()
                    }
                  }
                )
              }}
              onReject={(verificationNotes) => {
                rejectDocumentMutation.mutate(
                  { id: selectedDocument.id, verificationNotes },
                  {
                    onSuccess: () => {
                      handleDocumentReview()
                    }
                  }
                )
              }}
              isVerifying={verifyDocumentMutation.isPending}
              isRejecting={rejectDocumentMutation.isPending}
            />
          ) : (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
              <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No document selected</h3>
              <p className="mt-1 text-sm text-gray-500">
                Select a document from the list to review
              </p>
            </div>
          )}
        </div>
      </div>
      </>
      )}
    </div>
  )
}